import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Button,
  Alert
} from '@mui/material';
import axiosInstance from '../utils/axiosInstance';

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'review_requested', label: 'Review Requested' },
  { value: 'certified', label: 'Certified' },
  { value: 'archived', label: 'Archived' },
];

const formatStatus = (value = '') => (
  String(value || 'draft')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const statusColor = (status = '') => {
  if (status === 'certified') {
    return 'success';
  }
  if (status === 'review_requested') {
    return 'warning';
  }
  if (status === 'archived') {
    return 'default';
  }
  return 'primary';
};

const itemLabel = (item) => {
  if (item === null || typeof item === 'undefined') {
    return '';
  }
  if (typeof item === 'string') {
    return item;
  }
  if (typeof item === 'object') {
    return item.label || item.summary || item.name || item.asset_name || item.card_key || item.certification || item.id || JSON.stringify(item);
  }
  return String(item);
};

const ChipsSection = ({ title, items = [] }) => {
  const visible = Array.isArray(items) ? items.filter((item) => itemLabel(item)) : [];
  if (!visible.length) {
    return null;
  }
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{title}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {visible.map((item, idx) => (
          <Chip key={`${title}-${idx}`} label={itemLabel(item)} sx={{ maxWidth: '100%' }} />
        ))}
      </Stack>
    </Box>
  );
};

const TDPDetail = ({ tdp, onTdpUpdated }) => {
  const [status, setStatus] = useState(tdp?.status || 'draft');
  const [savingStatus, setSavingStatus] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    setStatus(tdp?.status || 'draft');
  }, [tdp?.status]);

  if (!tdp) return null;

  const previewImage = tdp.attached_files?.find(f => f.file_type === 'part_image')?.signed_url;
  const canEdit = Boolean(tdp.can_edit);
  const saveStatus = async () => {
    setSavingStatus(true);
    setError('');
    try {
      const response = await axiosInstance.patch(`/tdps/${encodeURIComponent(tdp.tdp_id)}`, { status });
      onTdpUpdated?.(response.data?.tdp);
    } catch (err) {
      console.error('Unable to update TDP status', err);
      setError(err?.response?.data?.message || 'Unable to update this xTDP right now.');
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>{tdp.name || tdp.tdp_title}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip color={statusColor(tdp.status)} label={formatStatus(tdp.status)} />
            {tdp.source === 'shared' ? <Chip variant="outlined" label="Shared" /> : null}
            {tdp.is_cui ? <Chip color="warning" variant="outlined" label="CUI" /> : null}
          </Stack>
        </Box>
        {canEdit ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ minWidth: { md: 360 } }}>
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              fullWidth
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={saveStatus} disabled={savingStatus || status === tdp.status}>
              {savingStatus ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography><strong>Version:</strong> {tdp.version || 'Unversioned'}</Typography>
            <Typography><strong>Designed By:</strong> {tdp.designed_by}</Typography>
            <Typography><strong>Approved By:</strong> {tdp.approved_by}</Typography>
            <Typography><strong>Owning Org:</strong> {tdp.amx_owning_org_id}</Typography>
            <Typography><strong>CAD Program:</strong> {tdp.cad_program}</Typography>
            <Typography><strong>Units:</strong> {tdp.dimensions_units}</Typography>
            <Typography><strong>Created:</strong> {new Date(tdp.created_at).toLocaleString()}</Typography>
            <Typography><strong>Updated:</strong> {new Date(tdp.updated_at).toLocaleString()}</Typography>
            <Typography sx={{ wordBreak: 'break-all' }}><strong>TDP ID:</strong> {tdp.tdp_id}</Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <ChipsSection title="Standards" items={tdp.standards} />
              <ChipsSection title="Specifications" items={tdp.specifications} />
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            {previewImage ? (
              <Box>
                <Typography variant="subtitle1">Preview Image</Typography>
                <img
                  src={previewImage}
                  alt="Part Preview"
                  style={{ maxWidth: '100%', borderRadius: 8 }}
                />
              </Box>
            ) : (
              <Typography>No preview image available</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Assets, Build History, And Certifications</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChipsSection title="Referenced Assets" items={tdp.asset_refs} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChipsSection title="Telemetry References" items={tdp.telemetry_refs} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChipsSection title="Build History" items={tdp.build_history} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChipsSection title="Certifications" items={tdp.certifications} />
          </Grid>
          <Grid item xs={12}>
            <ChipsSection title="Related Exchange Cards" items={tdp.related_exchange_cards} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6">Files</Typography>
        {tdp.artifact_files?.length ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {tdp.artifact_files.map((f) => (
              <Box key={f.file_id} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{f.file_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {f.content_type || 'application/octet-stream'} {f.file_type ? ` / ${f.file_type}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No managed artifact files are linked to this xTDP.</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Parts List</Typography>
        {tdp.parts_list?.map((p, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography><strong>Part {i + 1}</strong></Typography>
            <Typography>Qty: {p.qty}</Typography>
            <Typography>Name: {p.part_name}</Typography>
            <Typography>Part #: {p.part_number}</Typography>
            <Typography>Material: {p.material}</Typography>
            <Typography>Weight: {p.estimated_weight_lbs} lbs</Typography>
            <Typography>File Ref: {p.ref_file_id}</Typography>
            {p.specifications?.length > 0 && (
              <Box>
                <Typography>Specs:</Typography>
                {p.specifications.map((s, idx) => <Chip key={idx} label={s} sx={{ m: 0.5 }} />)}
              </Box>
            )}
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default TDPDetail;
